import { useTranslation } from 'react-i18next';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import WorkingCapacityForm from './WorkingCapacityForm';
import DeleteModal from '@shared/components/DeleteModal';
import { personService } from '@shared/services/personService';
import { useParams } from 'react-router-dom';
import PreviousPage from '@shared/components/PreviousPage';

export default function WorkingCapacity() {
    const { t } = useTranslation();
    const { id: personId } = useParams();
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const [selectedData, setSelectedData] = useState(null);
    const [data, setData] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [person, setPerson] = useState(location.state?.person || null);


    const fetchData = async () => {
        try {
            const response = await personService.getWorkingCapacity(personId);
            setData(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        if (!person && personId) {
            const fetchPerson = async () => {
                try {
                    const response = await personService.getPersonById(personId);
                    setPerson(response);
                } catch (error) {
                    console.error('Error fetching person:', error);
                }
            };
            fetchPerson();
        }
    }, [personId, person]);


    useEffect(() => {
        fetchData();
    }, [personId]);

    const handleOpenModal = (type) => {
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedData(null);
    };

    const handleConfirm = async (formData) => {
        try {
            const payload = {
                personId: parseInt(personId),
                fromDate: formData.fromDate,
                toDate: formData.toDate,
                percent: parseInt(formData.percent),
                oldPercent: null,
                docId: formData.docId,  // Add docId
                document: formData.document, // Keep document name
                orderNr: formData.orderNr,
                orderDate: formData.orderDate
            };


            if (modalState.type === 'add') {
                await personService.addWorkingCapacity(payload);
            } else if (modalState.type === 'edit') {
                await personService.updateWorkingCapacity(selectedData.id, payload);
            }
            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving data:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await personService.deleteWorkingCapacity(selectedData.id);
            fetchData();
            setSelectedData(null);
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting data:', error);
        }
    };

    const handleDeleteClick = () => {
        if (selectedData) {
            setShowDeleteModal(true);
        }
    };

    const handleCheckboxChange = (item) => {
        setSelectedData(selectedData?.id === item.id ? null : item);
    };

    return (
        <div className="working-capacity-container flex-column width-70">
            <div className="table-header">
                <div className="title-section">
                    {person ? (
                        <h1 className="main-heading">
                            {t('persons.workingCapacity.title', {
                                name: [person.name, person.fathersName, person.surname]
                                    .filter(Boolean)
                                    .join(' ')
                            })}
                        </h1>
                    ) : (
                        <h1 className="main-heading">Loading...</h1>
                    )}
                </div>
                <div className="controls-section">
                    <div className="header-icons">
                        <PreviousPage />

                        <button
                            className="icon-button"
                            onClick={() => handleOpenModal('add')}
                            title={t('common.add')}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                        <button
                            className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                            onClick={() => selectedData && handleOpenModal('edit')}
                            title={t('common.edit')}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                            className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                            onClick={handleDeleteClick}
                            disabled={!selectedData}
                            title={t('common.delete')}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="table-responsive custom-table-container">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th className="radio-column">
                                <div className="checkbox-wrapper">
                                </div>
                            </th>
                            <th className="sortable-header">
                                {t('persons.workingCapacity.startDate')}
                            </th>
                            <th>
                                {t('persons.workingCapacity.endDate')}
                            </th>
                            <th>
                                {t('persons.workingCapacity.percentage')}
                            </th>
                            <th>
                                {t('persons.workingCapacity.description')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item) => (
                            <tr key={item.id}>
                                <td className="radio-column">
                                    <div className="checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            checked={selectedData?.id === item.id}
                                            onChange={() => handleCheckboxChange(item)}
                                        />
                                    </div>
                                </td>
                                <td>{item.fromDate}</td>
                                <td>{item.toDate || 'Пожизнено'}</td>
                                <td>{item.percent}%</td>
                                <td>{item.document}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modalState.visible && (
                <WorkingCapacityForm
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    editData={modalState.type === 'edit' ? selectedData : null}
                    personId={personId}
                />
            )}

            {showDeleteModal && (
                <DeleteModal
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteConfirm}
                    name=""
                    entityType="record"
                />
            )}
        </div>
    );
}
